// Token refresh single-flight primitive shared by the apiClient 401 interceptor
// and the WebSocket reconnect refresh (useWebSocket). The outcome is modelled as data (a
// discriminated union) rather than control flow so the terminal-vs-transient
// distinction survives the shared in-flight promise: an unexpected throw
// defaults to `transient` (the safe direction — never auto-logout).

export type RefreshOutcome =
  | { status: 'success'; accessToken: string }
  | {
      status: 'terminal';
      reason: 'no_handle' | 'handle_invalid' | 'handle_expired' | 'grant_revoked';
    }
  | { status: 'transient' };

type TokenRefreshFn = () => Promise<RefreshOutcome>;

const TRANSIENT: RefreshOutcome = { status: 'transient' };

let _refreshFn: TokenRefreshFn | null = null;
let _inflightRefresh: Promise<RefreshOutcome> | null = null;

export function registerTokenRefresher(fn: TokenRefreshFn | null) {
  // Only drop an in-flight refresh when the function identity actually changes.
  // A provider re-render that re-registers the SAME (memoized) fn must not reset
  // the in-flight promise, or it would defeat single-flight and let a second
  // concurrent refresh fire (→ Auth0 reuse-detection family revocation).
  if (fn === _refreshFn) return;
  _refreshFn = fn;
  _inflightRefresh = null;
}

export async function refreshToken(): Promise<RefreshOutcome> {
  if (!_refreshFn) return { status: 'terminal', reason: 'no_handle' };
  if (_inflightRefresh) return _inflightRefresh;

  _inflightRefresh = _refreshFn()
    .catch(() => TRANSIENT)
    .finally(() => {
      _inflightRefresh = null;
    });

  return _inflightRefresh;
}
